// Modales de escritura del inventario de tarjetas PCF: entrada de lote y baja.
// Con `storeOptions` el operador elige la tienda; sin ella, la tienda viene fija.
import { useState } from 'react';
import { toast } from 'sonner';
import { Button, Input, Modal, Select, type SelectOption } from '@/kit';
import type { CardWriteOffReasonEnum } from '@fiado/type-kit/bin/retailCards/enums/CardWriteOffReasonEnum';
import { userMessageOf } from '@/api/errors';
import { useRegisterCardBatch, useWriteOffCardStock } from '../api/useRetailCards';
import { CARD_WRITE_OFF_OPTIONS } from '../constants';
import { writeOffErrorMessage } from '../writeOffErrors';

function StoreField({
    storeId,
    options,
    onChange,
}: {
    storeId: string;
    options: SelectOption[] | undefined;
    onChange: (storeId: string) => void;
}) {
    if (!options) return null;
    return (
        <Select
            label="Tienda"
            value={storeId}
            onChange={onChange}
            options={options}
            placeholder="Selecciona una tienda…"
            required
        />
    );
}

export function RegisterCardBatchModal({
    retailerId,
    storeId: initialStoreId,
    storeOptions,
    onClose,
    onRegistered,
}: {
    retailerId: string;
    storeId: string;
    storeOptions?: SelectOption[];
    onClose: () => void;
    onRegistered?: () => void;
}) {
    const [storeId, setStoreId] = useState(initialStoreId);
    const register = useRegisterCardBatch(retailerId, storeId);
    const [quantity, setQuantity] = useState('');
    const [supplierBatchRef, setSupplierBatchRef] = useState('');
    const [notes, setNotes] = useState('');

    const units = Number(quantity);
    const validQuantity = Number.isInteger(units) && units > 0;
    const validRef = supplierBatchRef.trim().length > 0;
    const canSubmit = storeId !== '' && validQuantity && validRef;

    const submit = (): void => {
        if (!canSubmit) return;
        register.mutate(
            {
                storeId,
                quantity: units,
                supplierBatchRef: supplierBatchRef.trim(),
                notes: notes.trim() || null,
            },
            {
                onSuccess: () => {
                    toast.success('Lote registrado');
                    onRegistered?.();
                    onClose();
                },
                onError: (e) => toast.error(userMessageOf(e)),
            },
        );
    };

    return (
        <Modal isOpen onClose={onClose} title="Registrar entrada de lote" size="md">
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    submit();
                }}
                className="space-y-4"
            >
                <StoreField storeId={storeId} options={storeOptions} onChange={setStoreId} />
                <Input
                    label="Cantidad de tarjetas"
                    type="number"
                    min={1}
                    step={1}
                    inputMode="numeric"
                    value={quantity}
                    onChange={(e) => {
                        setQuantity(e.target.value);
                    }}
                    hint="Las tarjetas llegan en sobres sellados: se cuenta el paquete, no se capturan números."
                    required
                />
                <Input
                    label="Folio de la remesa"
                    value={supplierBatchRef}
                    onChange={(e) => {
                        setSupplierBatchRef(e.target.value);
                    }}
                    hint="Viene en el documento del proveedor. Es lo que distingue dos entradas iguales."
                    required
                />
                <Input
                    label="Notas (opcional)"
                    value={notes}
                    onChange={(e) => {
                        setNotes(e.target.value);
                    }}
                />
                <div className="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={onClose}>
                        Cancelar
                    </Button>
                    <Button type="submit" disabled={!canSubmit} isLoading={register.isPending}>
                        Registrar lote
                    </Button>
                </div>
            </form>
        </Modal>
    );
}

export function WriteOffCardsModal({
    retailerId,
    storeId: initialStoreId,
    storeOptions,
    availableIn,
    onClose,
    onSettled,
}: {
    retailerId: string;
    storeId: string;
    storeOptions?: SelectOption[];
    /** Tarjetas disponibles en la tienda elegida. */
    availableIn: (storeId: string) => number;
    onClose: () => void;
    /** Tras éxito o error: un 409 también deja el saldo de pantalla desactualizado. */
    onSettled?: () => void;
}) {
    const [storeId, setStoreId] = useState(initialStoreId);
    const writeOff = useWriteOffCardStock(retailerId, storeId);
    const [quantity, setQuantity] = useState('');
    const [reason, setReason] = useState('');
    const [notes, setNotes] = useState('');
    // Clave de idempotencia del intento: se genera al abrir y solo cambia con la tienda.
    const [operationId, setOperationId] = useState(() => crypto.randomUUID());

    const available = storeId === '' ? 0 : availableIn(storeId);
    const units = Number(quantity);
    const validQuantity = Number.isInteger(units) && units > 0 && units <= available;
    const canSubmit = storeId !== '' && validQuantity && reason !== '';

    const submit = (): void => {
        if (!canSubmit) return;
        writeOff.mutate(
            {
                quantity: units,
                reason: reason as CardWriteOffReasonEnum,
                operationId,
                notes: notes.trim() || null,
            },
            {
                onSuccess: () => {
                    onSettled?.();
                    toast.success('Baja registrada');
                    onClose();
                },
                onError: (e) => {
                    onSettled?.();
                    const { message, alreadyApplied } = writeOffErrorMessage(e);
                    toast.error(message);
                    // El 409 dice que la baja anterior YA se aplicó: abierto invita a otro descuento.
                    if (alreadyApplied) onClose();
                },
            },
        );
    };

    return (
        <Modal isOpen onClose={onClose} title="Dar de baja tarjetas" size="md">
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    submit();
                }}
                className="space-y-4"
            >
                <StoreField
                    storeId={storeId}
                    options={storeOptions}
                    onChange={(next) => {
                        setStoreId(next);
                        setOperationId(crypto.randomUUID());
                    }}
                />
                <Input
                    label="Cantidad"
                    type="number"
                    min={1}
                    max={available}
                    step={1}
                    inputMode="numeric"
                    value={quantity}
                    onChange={(e) => {
                        setQuantity(e.target.value);
                    }}
                    error={
                        quantity !== '' && storeId !== '' && !validQuantity
                            ? `Captura un número entre 1 y ${String(available)}.`
                            : undefined
                    }
                    hint={storeId === '' ? undefined : `Disponibles ahora: ${String(available)}.`}
                    required
                />
                <Select
                    label="Motivo"
                    value={reason}
                    onChange={setReason}
                    options={CARD_WRITE_OFF_OPTIONS}
                    placeholder="Selecciona un motivo…"
                    required
                />
                <Input
                    label="Notas (opcional)"
                    value={notes}
                    onChange={(e) => {
                        setNotes(e.target.value);
                    }}
                />
                <div className="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={onClose}>
                        Cancelar
                    </Button>
                    <Button
                        type="submit"
                        variant="destructive"
                        disabled={!canSubmit}
                        isLoading={writeOff.isPending}
                    >
                        Dar de baja
                    </Button>
                </div>
            </form>
        </Modal>
    );
}
